import config from '../../config';
import { getAuthHeaders, handleResponse } from './common';

const buildNotificationPreferencesUrl = (organizationId, userReferenceId) => {
    if (organizationId == null || organizationId === '') {
        throw new Error('organizationId is required');
    }

    if (typeof userReferenceId !== 'string' || !userReferenceId.trim()) {
        throw new Error('userReferenceId is required');
    }

    return `${config.api.url}/organization/${encodeURIComponent(String(organizationId))}/users/${encodeURIComponent(userReferenceId.trim())}/notification-preferences`;
};

export const ORGANIZATION_STAFF_INVITE_RESCIND_SUPPORTED = true;

/**
 * Organization Users API
 * Handles fetching organization users/team members and clients
 */
const organizationUsersApi = {
    /**
     * List all users in an organization (team members only, excludes clients)
     * @param {number} organizationId - Organization ID
     * @returns {Promise<Array>} - Array of organization users
     */
    listOrganizationUsers: async (organizationId) => {
        const url = `${config.api.url}/organization/${organizationId}/users`;
        const headers = getAuthHeaders();
        const res = await fetch(url, { headers });
        return await handleResponse(res);
    },
    
    /**
     * List all clients in an organization
     * @param {number} organizationId - Organization ID
     * @returns {Promise<Array>} - Array of organization clients
     */
    listOrganizationClients: async (organizationId) => {
        const url = `${config.api.url}/organization/${organizationId}/clients`;
        const headers = getAuthHeaders();
        const res = await fetch(url, { headers });
        return await handleResponse(res);
    },

    /**
     * Resend a practice invitation to an organization user (non-client team member).
     * POST /organization/{organizationId}/users/{userId}/resend-invite
     * @param {number} organizationId - Organization ID
     * @param {number|string} userId - User ID
     */
    resendOrgUserInvite: async (organizationId, userId) => {
        const url = `${config.api.url}/organization/${organizationId}/users/${encodeURIComponent(userId)}/resend-invite`;
        const res = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return await handleResponse(res);
    },

    /**
     * Rescind a pending staff invitation (invalidates the invite link; does not delete the user).
     * POST /organization/{organizationId}/users/{userReferenceId}/rescind-invite
     */
    rescindOrgUserInvite: async (organizationId, userReferenceId) => {
        if (typeof userReferenceId !== 'string' || !userReferenceId.trim()) {
            throw new Error('userReferenceId is required');
        }
        const url = `${config.api.url}/organization/${encodeURIComponent(String(organizationId))}/users/${encodeURIComponent(userReferenceId.trim())}/rescind-invite`;
        const res = await fetch(url, {
            method: 'POST',
            headers: getAuthHeaders()
        });
        return await handleResponse(res);
    },

    /**
     * Remove a member from an organization.
     * DELETE /organization/{organizationId}/users/{userId}
     * @param {number} organizationId - Organization ID
     * @param {number|string} userId - User ID or referenceId
     */
    removeOrganizationUser: async (organizationId, userId) => {
        const url = `${config.api.url}/organization/${organizationId}/users/${encodeURIComponent(String(userId))}`;
        const res = await fetch(url, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return await handleResponse(res);
    },

    previewMembershipRecovery: async ({ organizationReferenceId, userReferenceId, organizationRole }) => {
        const query = new URLSearchParams({ organizationReferenceId, userReferenceId });
        if (organizationRole) query.set('organizationRole', organizationRole);
        const res = await fetch(`${config.api.url}/membership-recovery?${query}`, {
            headers: getAuthHeaders()
        });
        return await handleResponse(res);
    },

    applyMembershipRecovery: async (request) => {
        const res = await fetch(`${config.api.url}/membership-recovery`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(request)
        });
        return await handleResponse(res);
    },

    /**
     * Get the organization-managed push preferences for one staff member.
     * The member identifier must be User.ReferenceId, never the integer database ID.
     */
    getNotificationPreferences: async (organizationId, userReferenceId) => {
        const res = await fetch(buildNotificationPreferencesUrl(organizationId, userReferenceId), {
            headers: getAuthHeaders()
        });
        return await handleResponse(res);
    },

    /**
     * Replace the organization-managed push preferences for one staff member.
     * @param {number|string} organizationId
     * @param {string} userReferenceId - User.ReferenceId
     * @param {{ preferences: Array<{ eventKey: string, scope: string }> }} payload
     */
    updateNotificationPreferences: async (organizationId, userReferenceId, payload) => {
        const res = await fetch(buildNotificationPreferencesUrl(organizationId, userReferenceId), {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        return await handleResponse(res);
    }
};

export default organizationUsersApi;
